/**
 * Shared nudge-channel types — the single source of truth for the in-app nudge
 * wire contract, imported by BOTH the backend (get-nudge / record-nudge-event
 * actions, registry) and the frontend (RTK endpoints + NudgeBanner). Vendored
 * via the bt-common submodule; do not re-declare these shapes on either side.
 *
 * Convention mirrors bt-common/scheduling/*.types.ts.
 */

/** Registered nudge ids. */
export type NudgeId =
  | 'depth_upgrade_2026q3'
  // BE#147/M1 — engine-migration nudge, branched on Gateway entitlement:
  | 'engine_gateway_upsell_2026q3'   // free/Basic/Standard → switch to the always-on Gateway (Pro upsell)
  | 'engine_gateway_setup_2026q3'    // already-Pro (owns it, never set up) → finish setting up your Gateway
  | 'trial_engine_connect_2026q4'    // #137c — trialist who hasn't linked WhatsApp yet → finish connecting (flag OFF by default)
  | 'first_week_return'
  | 'ext_update_2026q4'               // #478 - outdated extension: ask the owner to update
  | 'trial_ending_2026q4'           // #690 — any live trialist in the last 2 days of the trial → upgrade (Pro if a Gateway was connected, else Basic)
  | 'referral_milestone_2026q4'      // #113 — step-4 in-app return prompt (guided arm only; no holdout — the arm IS the experiment)
  | 'ext_welcome_2026q4'             // #693 — one-time welcome modal after an extension signup (client-rendered; events only, never served by get-nudge)
  | 'review_ask_sends_2026q4'        // #787 — the user's 50th confirmed send → ask for a Chrome Web Store review (banner)
  | 'review_ask_campaign_2026q4'     // #787 — a finished campaign with 20+ recipients and 90%+ delivered → ask for a review (card on the finish screen)
  | 'review_ask_renewal_2026q4';     // #787 — long-term API/MCP user, banner at their next login after a successful renewal

/** Experiment arm. Only `send` ever reaches a client payload; `holdout` is
 *  server-recorded (nudge_withheld) and never rendered. */
export type NudgeArm = 'send' | 'holdout';

/** Where a nudge renders / where an event originated. */
export type NudgeSurface = 'web' | 'extension';

/** All nudge event names. `nudge_withheld` is SERVER-emitted only (holdout
 *  exposure) and is deliberately excluded from the client allowlist below. */
export type NudgeEventName =
  | 'nudge_shown'
  | 'nudge_clicked'
  | 'nudge_dismissed'
  | 'nudge_withheld';

/** The events a client (web/extension) is allowed to record. `nudge_withheld`
 *  is NOT here — a client must not be able to forge holdout exposure. Both the
 *  backend record-nudge-event allowlist and the frontend event helper use this. */
export const NUDGE_CLIENT_EVENT_NAMES = [
  'nudge_shown',
  'nudge_clicked',
  'nudge_dismissed',
] as const;

/** Client-recordable event names (derived from the const so the two never drift). */
export type NudgeClientEventName = (typeof NUDGE_CLIENT_EVENT_NAMES)[number];

/** Props stamped on every nudge event, on both arms/surfaces. */
export interface NudgeEventProps {
  nudgeId: NudgeId;
  arm: NudgeArm;
  surface: NudgeSurface;
}

/** The call-to-action rendered in the banner. */
export interface NudgeCta {
  label: string;
  url: string;
}

/** The payload the backend serves and the banner renders. `arm` is always
 *  `send` on a served payload (holdout users get a null payload). */
export interface NudgePayload {
  nudgeId: NudgeId;
  headline: string;
  body: string;
  cta: NudgeCta;
  arm: 'send';
  /** #787: numbers for client-side (i18n) copy. Only review asks set it. */
  vars?: NudgeVars;
}

/** The get-nudge action's response envelope: a payload, or null (not eligible /
 *  capped / dismissed / holdout / fail-closed). */
export interface GetNudgeResponse {
  nudge: NudgePayload | null;
}

// ---------------------------------------------------------------------------
// #787 — in-product review ask. One shared cap (server-side) covers both ids.
// ---------------------------------------------------------------------------

/** The review-ask nudge ids. They share one per-user cap (90 days apart, 2 per lifetime). */
export const REVIEW_ASK_NUDGE_IDS = ['review_ask_sends_2026q4', 'review_ask_campaign_2026q4', 'review_ask_renewal_2026q4'] as const;

export type ReviewAskNudgeId = (typeof REVIEW_ASK_NUDGE_IDS)[number];

export function isReviewAskNudgeId(id: unknown): id is ReviewAskNudgeId {
  return typeof id === 'string' && (REVIEW_ASK_NUDGE_IDS as readonly string[]).includes(id);
}

/** The Chrome Web Store listing: the one store link every surface uses. */
export const CHROME_WEB_STORE_LISTING_URL =
  'https://chromewebstore.google.com/detail/blueticks-whatsapp-schedu/adgnjhngogijkkppficiiepmjebijinl';

/** Chrome Web Store reviews tab: the one review link, for extension and web-app-only users alike. */
export const REVIEW_ASK_REVIEW_URL = `${CHROME_WEB_STORE_LISTING_URL}/reviews`;

/** Where a get-nudge call comes from. Absent = the app-wide banner; `campaign_finish` = the campaign finish card. */
export type NudgePlacement = 'campaign_finish';

/** The get-nudge request body. All fields optional: the banner call sends none. */
export interface GetNudgeRequest {
  placement?: NudgePlacement;
  /** Required with `campaign_finish`: the campaign whose finish screen is showing. */
  campaignId?: string;
  /**
   * #787: set by clients that can render a review ask. The server serves (and records, against the
   * weekly slot and the lifetime cap) a review ask ONLY when this is true, so an older client that
   * would drop it unseen never burns the user's ask.
   */
  supportsReviewAsk?: boolean;
}

/** Numbers the client needs to word a review ask in its own language (the server copy is English). */
export interface NudgeVars {
  /** review_ask_sends_2026q4: the send milestone that was crossed. */
  sendCount?: number;
  /** review_ask_campaign_2026q4: how many recipients the campaign had. */
  contactCount?: number;
}
